// Small hex-color helpers shared by anything that needs to tint or soften a
// raw hex string at render time rather than defining a second literal color
// for every shade (soft badge fills, dark-mode lifts of a color defined once
// elsewhere — e.g. defenseStrategies.ts's per-strategy stroke hexes). Kept
// in the theme layer, next to colors.ts, rather than duplicated at each call
// site — `brandedPalette` in brand.ts has its own private `hexToRgb` for the
// same reason; this is the shared, exported version other files should reach
// for first.

export function hexToRgb(hex: string): [number, number, number] {
  const clean = hex.replace('#', '');
  const r = parseInt(clean.slice(0, 2), 16);
  const g = parseInt(clean.slice(2, 4), 16);
  const b = parseInt(clean.slice(4, 6), 16);
  return [r, g, b];
}

// `#RRGGBB` -> `rgba(r, g, b, alpha)`. For soft tint fills (badge
// backgrounds, chip fills) where a hard-coded rgba() literal would drift
// from the hex it is supposed to be tinting.
export function hexWithAlpha(hex: string, alpha: number): string {
  const [r, g, b] = hexToRgb(hex);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

// Linear-blends two hex colors. amount 0 returns `hex`, 1 returns `toHex`.
export function mixHex(hex: string, toHex: string, amount: number): string {
  const [r1, g1, b1] = hexToRgb(hex);
  const [r2, g2, b2] = hexToRgb(toHex);
  const mix = (a: number, b: number) => Math.round(a + (b - a) * amount);
  const toChannel = (n: number) => n.toString(16).padStart(2, '0');
  return `#${toChannel(mix(r1, r2))}${toChannel(mix(g1, g2))}${toChannel(mix(b1, b2))}`;
}

// Perceived lightness of a hex color on a 0 (black) - 1 (white) scale, good
// enough to decide "is this too dark to read on a dark background" without
// pulling in a full color-space library.
export function relativeLuminance(hex: string): number {
  const [r, g, b] = hexToRgb(hex);
  return (0.299 * r + 0.587 * g + 0.114 * b) / 255;
}
